import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { AuthProvider, useAuth } from './context/AuthContext';
import Layout from './components/Layout';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import PublishArticle from './pages/PublishArticle';
import Articles from './pages/Articles';
import ZonalArticles from './pages/ZonalArticles';
import ReviewArticles from './pages/ReviewArticles';
import Ads from './pages/Ads';
import StickyNews from './pages/StickyNews';
import Notifications from './pages/Notifications';
import AuthorDetail from './pages/AuthorDetail';
import Moderation from './pages/Moderation';
import Analytics from './pages/Analytics';
import Settings from './pages/Settings';
import Manage from './pages/Manage';
import Comments from './pages/Comments';
import Users from './pages/Users';
import Admins from './pages/Admins';
import Contributors from './pages/Contributors';
import ContributorReview from './pages/ContributorReview';
import ContributorEarnings from './pages/ContributorEarnings';
import Withdrawals from './pages/Withdrawals';
import Partners from './pages/Partners';
import PartnerInstalls from './pages/PartnerInstalls';
import PartnerPayouts from './pages/PartnerPayouts';
import PartnerAnalytics from './pages/PartnerAnalytics';
import ArticleEngagementDetails from './pages/ArticleEngagementDetails';
import StatusManager from './pages/StatusManager';
import QuotesManager from './pages/QuotesManager';
import CategoryReport from './pages/CategoryReport';
import CityReport from './pages/CityReport';
import SocialMedia from './pages/SocialMedia';
import DailyHoroscope from './pages/DailyHoroscope';
import TeluguCalendar from './pages/TeluguCalendar';
import ContactMessages from './pages/ContactMessages';
import './App.css';

// Apply the saved theme immediately at module load, before any route
// renders. Layout's switch is the only place that *changes* it, but /login
// renders outside Layout entirely — without this, a fresh load or a
// logout would always reset the login screen to light regardless of the
// saved preference.
if (typeof document !== 'undefined') {
  document.documentElement.setAttribute('data-theme', localStorage.getItem('admin_theme') || 'light');
}

function PrivateRoute({ children }) {
  const { isAuthenticated } = useAuth();
  return isAuthenticated ? children : <Navigate to="/login" />;
}

function AppRoutes() {
  const { isAuthenticated } = useAuth();

  return (
    <Routes>
      <Route path="/login" element={isAuthenticated ? <Navigate to="/" /> : <Login />} />
      <Route path="/" element={<PrivateRoute><Layout /></PrivateRoute>}>
        <Route index element={<Dashboard />} />
        <Route path="users" element={<Users />} />
        <Route path="articles/new" element={<PublishArticle />} />
        <Route path="articles" element={<Articles />} />
        <Route path="articles/zonal" element={<ZonalArticles />} />
        <Route path="articles/engagement/:id" element={<ArticleEngagementDetails />} />
        <Route path="review-articles" element={<ReviewArticles />} />
        <Route path="contributors" element={<Contributors />} />
        <Route path="contributor-earnings" element={<ContributorEarnings />} />
        <Route path="contributor-review" element={<ContributorReview />} />
        <Route path="withdrawals" element={<Withdrawals />} />
        <Route path="partners" element={<Partners />} />
        <Route path="partner-installs" element={<PartnerInstalls />} />
        <Route path="partner-payouts" element={<PartnerPayouts />} />
        <Route path="partner-analytics" element={<PartnerAnalytics />} />
        <Route path="ads" element={<Ads />} />
        <Route path="sticky-news" element={<StickyNews />} />
        <Route path="status" element={<StatusManager />} />
        <Route path="quotes" element={<QuotesManager />} />
        <Route path="social-media" element={<SocialMedia />} />
        <Route path="horoscope" element={<DailyHoroscope />} />
        <Route path="telugu-calendar" element={<TeluguCalendar />} />
        <Route path="notifications" element={<Notifications />} />
        <Route path="authors/:type/:id" element={<AuthorDetail />} />
        <Route path="comments" element={<Comments />} />
        <Route path="moderation" element={<Moderation />} />
        <Route path="analytics" element={<Analytics />} />
        <Route path="manage" element={<Manage />} />
        <Route path="settings" element={<Settings />} />
        <Route path="admins" element={<Admins />} />
        <Route path="contact-messages" element={<ContactMessages />} />
        <Route path="reports/category" element={<CategoryReport />} />
        <Route path="reports/city" element={<CityReport />} />
      </Route>
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Toaster position="top-right" toastOptions={{
          style: { background: '#1a1a1a', color: '#fff', borderRadius: '10px' },
        }} />
        <AppRoutes />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
